import { BaseResourceCloudPlatform, BaseResourceTaggabilityItem, IQueryExpr } from '@apis/Resources/model';
import { EnsureGcpAuthenticated } from '@root/Components/Connections/GcpConnector';
import { BaseResource } from '@root/Components/Resources/ResourcesGrid';
import { useState } from 'react';
import { singleton } from 'tsyringe';
import { postResourcesQuery } from '@apis/Resources';
import { queryBuilder } from '@root/Services/QueryExpr';

export type DelegatedOperation = 'tag' | 'label';

export interface DelegatedAuthResult {
    success: boolean;
    delegated: boolean;
}

@singleton()
export class DelegatedTagAuthService {
    public requiresDelegatedAuth(resource: BaseResource | null | undefined, op: DelegatedOperation): boolean {
        if (!resource) return false;

        if (resource.CloudPlatform !== 'Gcp') {
            return false;
        }

        if (op === 'tag') {
            return resource.Taggability?.includes('DelegatedTaggable') || false;
        }

        if (op === 'label') {
            return resource.Taggability?.includes('DelegatedLabelable') || false;
        }

        return false;
    }

    public serviceEnabled(resource: BaseResource | null | undefined, op: DelegatedOperation): boolean {
        if (!resource) return false;

        if (resource.CloudPlatform !== 'Gcp') {
            return false;
        }

        if (op === 'tag') {
            return (
                resource.Taggability?.includes(BaseResourceTaggabilityItem.DelegatedTaggable) ||
                resource.Taggability?.includes(BaseResourceTaggabilityItem.Taggable) ||
                false
            );
        }

        if (op === 'label') {
            return (
                resource.Taggability?.includes(BaseResourceTaggabilityItem.DelegatedLabelable) ||
                resource.Taggability?.includes(BaseResourceTaggabilityItem.Labelable) ||
                false
            );
        }

        return false;
    }

    private async runPlatformHandler(platform: BaseResourceCloudPlatform, op: DelegatedOperation): Promise<void> {
        switch (platform) {
            case 'Gcp':
                await EnsureGcpAuthenticated();
                return;
            default:
                throw new Error(`No delegated auth handler registered for platform ${platform}`);
        }
    }

    async ensureDelegatedAuth(resource: BaseResource | null | undefined, op: DelegatedOperation): Promise<boolean> {
        if (!this.requiresDelegatedAuth(resource, op)) {
            return true;
        }

        try {
            await this.runPlatformHandler(resource?.CloudPlatform!, op);
            return true;
        } catch (err) {
            console.error('Delegated auth failed', err);
            return false;
        }
    }

    async ensureTaggingAuth(resource: BaseResource | null | undefined): Promise<boolean> {
        return this.ensureDelegatedAuth(resource, 'tag');
    }

    async ensureLabelingAuth(resource: BaseResource | null | undefined): Promise<boolean> {
        return this.ensureDelegatedAuth(resource, 'label');
    }

    requiresAnyDelegatedAuth(resources: BaseResource[], op: DelegatedOperation): boolean {
        return resources.some((resource) => this.requiresDelegatedAuth(resource, op));
    }

    async requiresAnyDelegatedAuthByIds(
        resourceIds: Array<{ ResourceId?: string | null; ResourceType?: string | null }>,
        op: DelegatedOperation
    ): Promise<boolean> {
        try {
            const resourcePromises = resourceIds.map(async (rid) => {
                if (!rid.ResourceId || !rid.ResourceType) return null;
                try {
                    const query: IQueryExpr = {
                        Operation: 'and',
                        Operands: [
                            { Operation: 'eq', Operands: [{ Field: 'Id' }, { Value: rid.ResourceId }] },
                            { Operation: 'eq', Operands: [{ Field: 'ResourceType' }, { Value: rid.ResourceType }] },
                        ],
                    };
                    const resource = (await postResourcesQuery({
                        Where: query,
                        Take: 1,
                    })) as any;
                    return resource?.Results?.[0] as BaseResource | null;
                } catch (err) {
                    console.error('DelegatedTagAuthService - Error fetching resource:', err);
                    return null;
                }
            });

            const resources = (await Promise.all(resourcePromises)).filter((r): r is BaseResource => r !== null);
            return this.requiresAnyDelegatedAuth(resources, op);
        } catch (err) {
            console.error('Failed to check for delegated auth requirements by IDs', err);
            return false;
        }
    }

    async requiresDelegatedAuthByQuery(query: IQueryExpr | undefined, op: DelegatedOperation): Promise<boolean> {
        const taggabilityValue = op === 'tag' ? 'DelegatedTaggable' : 'DelegatedLabelable';

        try {
            const delegatedAuthWhere: IQueryExpr = {
                Operation: 'and',
                Operands: [
                    { Operation: 'eq', Operands: [{ Field: 'CloudPlatform' }, { Value: 'Gcp' }] },
                    { Operation: 'contains', Operands: [{ Field: 'Taggability' }, { Value: taggabilityValue }] },
                ],
            };

            const countQuery = await queryBuilder<BaseResource>()
                .select((b) => ({ count: b.count() }))
                .build();

            if (query) {
                countQuery.Where = {
                    Operation: 'and',
                    Operands: [query, delegatedAuthWhere],
                };
            } else {
                countQuery.Where = delegatedAuthWhere;
            }

            const results = (await postResourcesQuery(countQuery)) as any;
            const count = results?.Results?.[0]?.count ?? 0;
            return count > 0;
        } catch (err) {
            console.error('Failed to check for delegated auth requirements', err);
            return false;
        }
    }

    private useDelegatedAuthInternal(op: DelegatedOperation) {
        const [isAuthenticating, setIsAuthenticating] = useState(false);
        const [authError, setAuthError] = useState<string | null>(null);

        const authenticate = async (resource: BaseResource | null | undefined): Promise<DelegatedAuthResult> => {
            if (!resource) {
                setIsAuthenticating(true);
                setAuthError(null);

                try {
                    await EnsureGcpAuthenticated();
                    setIsAuthenticating(false);
                    return { success: true, delegated: true };
                } catch (err: any) {
                    setAuthError(err?.message || err?.toString?.() || 'Failed to authenticate for delegated access.');
                    setIsAuthenticating(false);
                    return { success: false, delegated: true };
                }
            }

            const delegated = this.requiresDelegatedAuth(resource, op);
            if (!delegated) {
                return { success: true, delegated: false };
            }

            setIsAuthenticating(true);
            setAuthError(null);

            try {
                await this.runPlatformHandler(resource?.CloudPlatform!, op);
                setIsAuthenticating(false);
                return { success: true, delegated: true };
            } catch (err: any) {
                console.error('Delegated auth failed', err);
                setAuthError(err?.message || err?.toString?.() || 'Failed to authenticate for delegated access.');
                setIsAuthenticating(false);
                return { success: false, delegated: true };
            }
        };

        return {
            isAuthenticating,
            authError,
            authenticate,
            clearError: () => setAuthError(null),
        };
    }

    useTaggingAuth() {
        return this.useDelegatedAuthInternal('tag');
    }

    useLabelingAuth() {
        return this.useDelegatedAuthInternal('label');
    }
}
